import { getClubOrganizationName } from "./clubs";
import { REWARD_IDS } from "./constants";
import type {
  AchievementDefinition,
  AchievementId,
  AchievementProfile,
  GameStateV2,
  RewardId,
} from "./types";

export const ACHIEVEMENT_STORAGE_KEY = "football-roguelike-achievements-v1";

export const ACHIEVEMENTS: readonly AchievementDefinition[] = [
  {
    id: "legend",
    name: "Leyenda mundial",
    description: "Alcanza 90 de OVR en una carrera.",
    target: 90,
    unit: "OVR",
    icon: "mdi:star-four-points",
    art: "legend",
    unlockTiming: "during-run",
    reward: null,
  },
  {
    id: "goals",
    name: "Goleador histórico",
    description: "Marca 12 goles en una carrera.",
    target: 12,
    unit: "goles",
    icon: "mdi:soccer",
    art: "goals",
    unlockTiming: "during-run",
    reward: null,
  },
  {
    id: "club",
    name: "One Club Man",
    description: "Completa las cinco etapas sin cambiar de club.",
    target: 5,
    unit: "etapas",
    icon: "mdi:shield-star-outline",
    art: "club",
    unlockTiming: "career-end",
    reward: null,
  },
  {
    id: "creator",
    name: "Maestro de asistencias",
    description: "Consigue 10 asistencias en una carrera.",
    target: 10,
    unit: "asistencias",
    icon: "mdi:strategy",
    art: "creator",
    unlockTiming: "during-run",
    reward: null,
  },
  {
    id: "titles",
    name: "Cazador de títulos",
    description: "Gana los títulos de Consolidación, Élite y Legado en una misma carrera.",
    target: 3,
    unit: "títulos",
    icon: "mdi:trophy-outline",
    art: "titles",
    unlockTiming: "during-run",
    reward: null,
  },
  {
    id: "victories",
    name: "Ganador nato",
    description: "Consigue 25 victorias oficiales en una carrera.",
    target: 25,
    unit: "victorias",
    icon: "mdi:medal-outline",
    art: "goals",
    unlockTiming: "during-run",
    reward: null,
  },
  {
    id: "derbies",
    name: "Rey del derbi",
    description: "Gana 3 derbis en una misma carrera.",
    target: 3,
    unit: "derbis",
    icon: "mdi:fire",
    art: "titles",
    unlockTiming: "during-run",
    reward: null,
  },
  {
    id: "streak",
    name: "En estado de gracia",
    description: "Encadena 4 encuentros decisivos ganados.",
    target: 4,
    unit: "victorias seguidas",
    icon: "mdi:lightning-bolt",
    art: "legend",
    unlockTiming: "during-run",
    reward: null,
  },
  {
    id: "reputation",
    name: "Ídolo de la afición",
    description: "Alcanza 90 de prestigio en tu club.",
    target: 90,
    unit: "prestigio",
    icon: "mdi:account-star-outline",
    art: "club",
    unlockTiming: "during-run",
    reward: null,
  },
  {
    id: "collector",
    name: "Vestuario de museo",
    description: "Reúne 5 reliquias diferentes en tu build.",
    target: 5,
    unit: "reliquias",
    icon: "mdi:treasure-chest-outline",
    art: "creator",
    unlockTiming: "during-run",
    reward: null,
  },
  {
    id: "traveller",
    name: "Pasaporte lleno",
    description: "Defiende los colores de 3 clubes en una carrera.",
    target: 3,
    unit: "clubes",
    icon: "mdi:airplane",
    art: "club",
    unlockTiming: "during-run",
    reward: null,
  },
  {
    id: "complete",
    name: "Carrera para la historia",
    description: "Completa las cinco etapas de la carrera.",
    target: 1,
    unit: "carrera",
    icon: "mdi:crown-outline",
    art: "titles",
    unlockTiming: "career-end",
    reward: null,
  },
  {
    id: "allRounder",
    name: "Futbolista total",
    description: "Suma al menos 8 goles y 8 asistencias en una carrera.",
    target: 8,
    unit: "goles y asistencias",
    icon: "mdi:axis-arrow",
    art: "creator",
    unlockTiming: "during-run",
    reward: null,
  },
  {
    id: "realMadridTitle",
    name: "Noche blanca",
    description: "Gana un título defendiendo al Real Madrid.",
    target: 1,
    unit: "título",
    icon: "mdi:trophy-variant-outline",
    art: "titles",
    club: "Real Madrid",
    unlockTiming: "during-run",
    reward: null,
  },
  {
    id: "barcelonaCreator",
    name: "ADN Barça",
    description: "Reparte 5 asistencias en una misma etapa con Barcelona.",
    target: 5,
    unit: "asistencias",
    icon: "mdi:strategy",
    art: "creator",
    club: "Barcelona",
    unlockTiming: "during-run",
    reward: null,
  },
  {
    id: "liverpoolGoals",
    name: "This is Anfield",
    description: "Marca 5 goles en una misma etapa con Liverpool.",
    target: 5,
    unit: "goles",
    icon: "mdi:soccer",
    art: "goals",
    club: "Liverpool",
    unlockTiming: "during-run",
    reward: null,
  },
  {
    id: "bayernGrowth",
    name: "Mia san mia",
    description: "Mejora 7 puntos de OVR en una misma etapa con Bayern Munich.",
    target: 7,
    unit: "OVR ganados",
    icon: "mdi:chart-line",
    art: "legend",
    club: "Bayern Munich",
    unlockTiming: "during-run",
    reward: null,
  },
  {
    id: "bocaIdol",
    name: "La Bombonera late",
    description: "Alcanza 85 de prestigio en el club con Boca Juniors.",
    target: 85,
    unit: "prestigio",
    icon: "mdi:account-star-outline",
    art: "club",
    club: "Boca Juniors",
    unlockTiming: "during-run",
    reward: null,
  },
  {
    id: "napoliContributions",
    name: "El heredero del Diez",
    description: "Suma 7 goles y asistencias en una misma etapa con Napoli.",
    target: 7,
    unit: "contribuciones",
    icon: "mdi:handshake-outline",
    art: "creator",
    club: "Napoli",
    unlockTiming: "during-run",
    reward: null,
  },
  {
    id: "athleticLoyalty",
    name: "Lezama de por vida",
    description: "Completa las cuatro etapas de clubes sin abandonar Athletic Bilbao.",
    target: 4,
    unit: "etapas",
    icon: "mdi:shield-star-outline",
    art: "club",
    club: "Athletic Bilbao",
    unlockTiming: "career-end",
    reward: null,
  },
] as const;

interface AchievementStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const emptyAchievementProfile = (): AchievementProfile => ({ version: 1, unlocked: {}, discoveredRewards: {} });

export function parseAchievementProfile(value: unknown): AchievementProfile {
  if (!value || typeof value !== "object") return emptyAchievementProfile();
  const source = (value as { unlocked?: unknown }).unlocked;
  const discoveredSource = (value as { discoveredRewards?: unknown }).discoveredRewards;
  const unlocked: AchievementProfile["unlocked"] = {};
  if (source && typeof source === "object") {
    ACHIEVEMENTS.forEach(({ id }) => {
      const date = (source as Record<string, unknown>)[id];
      if (typeof date === "string") unlocked[id] = date;
    });
  }
  const discoveredRewards: AchievementProfile["discoveredRewards"] = {};
  if (discoveredSource && typeof discoveredSource === "object") {
    REWARD_IDS.forEach((id) => {
      const date = (discoveredSource as Record<string, unknown>)[id];
      if (typeof date === "string") discoveredRewards[id] = date;
    });
  }
  return { version: 1, unlocked, discoveredRewards };
}

export function loadAchievementProfile(storage: AchievementStorage): AchievementProfile {
  try {
    const raw = storage.getItem(ACHIEVEMENT_STORAGE_KEY);
    return raw ? parseAchievementProfile(JSON.parse(raw) as unknown) : emptyAchievementProfile();
  } catch {
    return emptyAchievementProfile();
  }
}

export function saveAchievementProfile(storage: AchievementStorage, profile: AchievementProfile): void {
  storage.setItem(ACHIEVEMENT_STORAGE_KEY, JSON.stringify(profile));
}

export function discoverRewards(
  profile: AchievementProfile,
  rewardIds: readonly RewardId[],
  discoveredAt = new Date().toISOString(),
): AchievementProfile {
  const undiscovered = rewardIds.filter((id) => !profile.discoveredRewards[id]);
  if (!undiscovered.length) return profile;
  const discoveredRewards = { ...profile.discoveredRewards };
  undiscovered.forEach((id) => { discoveredRewards[id] = discoveredAt; });
  return { ...profile, discoveredRewards };
}

export function rewardIdsDiscoveredInState(
  state: Pick<GameStateV2, "inventory" | "pendingScoutOffer">,
): RewardId[] {
  const discovered = new Set<RewardId>();
  REWARD_IDS.forEach((id) => {
    if ((state.inventory[id]?.count ?? 0) > 0) discovered.add(id);
  });
  state.pendingScoutOffer?.forEach((id) => discovered.add(id));
  return [...discovered];
}

type StageMetric = "goals" | "assists" | "titles" | "ovrGrowth" | "contributions";

function teamStagePeak(state: GameStateV2, club: string, metric: StageMetric): number {
  const value = (entry: GameStateV2["careerHistory"][number]) => {
    if (metric === "titles") return entry.titlesWon;
    if (metric === "ovrGrowth") return Math.max(0, entry.ovrEnd - entry.ovrStart);
    if (metric === "contributions") return entry.goals + entry.assists;
    return entry[metric];
  };
  const completed = state.careerHistory.filter((entry) => entry.team === club).map(value);
  if (state.season < 5 && state.club === club) {
    completed.push(
      metric === "goals" ? Math.max(0, state.goals - state.stageBaseline.goals)
        : metric === "assists" ? Math.max(0, state.assists - state.stageBaseline.assists)
          : metric === "titles" ? Math.max(0, state.titles - state.stageBaseline.titles)
            : metric === "ovrGrowth" ? Math.max(0, state.ovr - state.stageBaseline.ovr)
              : Math.max(0, state.goals - state.stageBaseline.goals) + Math.max(0, state.assists - state.stageBaseline.assists),
    );
  }
  return Math.max(0, ...completed);
}

export function achievementProgress(state: GameStateV2, id: AchievementId): number {
  switch (id) {
    case "legend": return state.ovr;
    case "goals": return state.goals;
    case "club": return state.oneClubEligible ? state.seasonsCompleted : 0;
    case "creator": return state.assists;
    case "titles": return state.titles;
    case "victories": return state.wins;
    case "derbies": return state.derbyWins;
    case "streak": return state.bestWinStreak;
    case "reputation": return state.clubReputation;
    case "collector": return Object.values(state.inventory).filter((stack) => stack?.kind === "relic" && stack.count > 0).length;
    case "traveller": return new Set([
      ...state.careerHistory.filter((entry) => entry.stage < 5).map((entry) => getClubOrganizationName(entry.team)),
      getClubOrganizationName(state.club),
    ]).size;
    case "complete": return state.careerHistory.some((entry) => entry.stage === 5 && entry.outcome === "completed") ? 1 : 0;
    case "allRounder": return Math.min(state.goals, state.assists);
    case "realMadridTitle": return teamStagePeak(state, "Real Madrid", "titles");
    case "barcelonaCreator": return teamStagePeak(state, "Barcelona", "assists");
    case "liverpoolGoals": return teamStagePeak(state, "Liverpool", "goals");
    case "bayernGrowth": return teamStagePeak(state, "Bayern Munich", "ovrGrowth");
    case "bocaIdol": return state.season < 5 && state.club === "Boca Juniors" ? state.clubReputation : 0;
    case "napoliContributions": return teamStagePeak(state, "Napoli", "contributions");
    case "athleticLoyalty": return new Set(state.careerHistory
      .filter((entry) => entry.stage < 5 && entry.team === "Athletic Bilbao" && entry.outcome === "completed")
      .map((entry) => entry.stage)).size;
  }
}

export function achievementEarnedInRun(state: GameStateV2, achievement: AchievementDefinition): boolean {
  if (achievement.id === "club") {
    return state.finished && state.oneClubEligible && state.seasonsCompleted >= achievement.target;
  }
  if (achievement.id === "complete") return state.finished && achievementProgress(state, achievement.id) >= achievement.target;
  if (achievement.id === "athleticLoyalty") {
    return state.finished && state.oneClubEligible && achievementProgress(state, achievement.id) >= achievement.target;
  }
  return achievementProgress(state, achievement.id) >= achievement.target;
}

export function newlyUnlockedAchievements(
  state: GameStateV2,
  profile: AchievementProfile,
): AchievementDefinition[] {
  return ACHIEVEMENTS.filter((achievement) =>
    !profile.unlocked[achievement.id] && achievementEarnedInRun(state, achievement));
}

export function unlockAchievements(
  profile: AchievementProfile,
  achievements: readonly AchievementDefinition[],
  unlockedAt = new Date().toISOString(),
): AchievementProfile {
  if (!achievements.length) return profile;
  const unlocked = { ...profile.unlocked };
  achievements.forEach(({ id }) => { unlocked[id] = unlockedAt; });
  return { ...profile, unlocked };
}
